/**
 * CotacaoTab - Componente para gerenciamento de cotações
 * Inclui funcionalidade de upload de NF-e (XML/PDF)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '@/hooks/use-toast';

const CotacaoTab = ({ cotacoes = [], onRefresh, usuario, showConfirmDialog }) => {
  // Helper para verificar se usuário tem permissões de Comprador/Administrador
  const temPermissaoComprador = () => {
    return usuario.tipo_usuario === 'Comprador' || usuario.tipo_usuario === 'Administrador';
  };

  const [showForm, setShowForm] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [showCotacaoDetail, setShowCotacaoDetail] = useState(false);
  const [selectedCotacao, setSelectedCotacao] = useState(null);
  const [showSubmitPrice, setShowSubmitPrice] = useState(false);
  const [precos, setPrecos] = useState({});
  const [editingCotacao, setEditingCotacao] = useState(null);
  const [precosInicializados, setPrecosInicializados] = useState(false); // Flag para controlar inicialização única
  const [outrasPropostasAberto, setOutrasPropostasAberto] = useState({}); // Controla quais produtos mostram outras propostas
  const [fornecedores, setFornecedores] = useState([]); // Lista de todos os fornecedores
  
  // Estados para modal de seleção de variações
  const [showModalVariacoes, setShowModalVariacoes] = useState(false);
  const [cotacaoParaEncerrar, setCotacaoParaEncerrar] = useState(null);
  const [variacoesSelecionadas, setVariacoesSelecionadas] = useState({}); // produto_id: nome_variacao
  const [searchTermCotacao, setSearchTermCotacao] = useState(''); // Termo de busca na visualização de cotação
  const [showEditarVencedorModal, setShowEditarVencedorModal] = useState(false);
  const [showAdicionarVencedorManualModal, setShowAdicionarVencedorManualModal] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState(null);
  const [cotacaoEditando, setCotacaoEditando] = useState(null);
  const [vencedorEditando, setVencedorEditando] = useState({
    fornecedor_id: '',
    nome_fornecedor: '',
    valor_caixa: 0,
    quantidade_kg: 0
  });
  const [vencedorManualDados, setVencedorManualDados] = useState({
    fornecedor_nome: '',
    valor_caixa: 0,
    quantidade_kg: 0,
    variacao: ''
  });
  const [fornecedoresVinculados, setFornecedoresVinculados] = useState([]);
  const [todosFornecedores, setTodosFornecedores] = useState([]);
  const [formData, setFormData] = useState({
    nome: '',
    data_encerramento: '',
    hora_encerramento: '',
    baseada_em_pedidos: false,
    pedidos_ids: []
  });
  const [precosFornecedorSalvos, setPrecosFornecedorSalvos] = useState({}); // Preços salvos pelo fornecedor na aba PRODUTOS
  
  // Estados para Upload de NF-e (XML/PDF)
  const [showUploadNfeModal, setShowUploadNfeModal] = useState(false);
  const [nfeData, setNfeData] = useState(null);
  const [nfeMatches, setNfeMatches] = useState([]);
  const [nfeUploading, setNfeUploading] = useState(false);
  const [nfeApplying, setNfeApplying] = useState(false);
  const nfeFileInputRef = React.useRef(null);

  // Função para recarregar apenas a cotação selecionada (sem sair da tela)
  const recarregarCotacaoAtual = async (cotacaoId) => {
    try {
      // Buscar dados atualizados da cotação específica
      const response = await axios.get(`/cotacoes/${cotacaoId}`);
      const cotacaoAtualizada = response.data;
      
      // Atualizar a cotação selecionada com os novos dados
      setSelectedCotacao(cotacaoAtualizada);
      
      // Atualizar também a lista local de cotações para refletir a mudança
      // Buscar lista atualizada em background
      try {
        const cotacoesResponse = await axios.get('/cotacoes');
        // Não chamar onRefresh para evitar re-render completo do componente
        // Apenas atualizamos os dados localmente se necessário
      } catch (e) {
        console.error('Erro ao atualizar lista de cotações:', e);
      }
      
      return cotacaoAtualizada;
    } catch (error) {
      console.error('Erro ao recarregar cotação:', error);
      return null;
    }
  };

  // Carregar preços salvos do fornecedor (se for fornecedor)
  useEffect(() => {
    const loadPrecosFornecedor = async () => {
      if (usuario.tipo_usuario === 'Fornecedor') {
        try {
          const response = await axios.get('/fornecedor/produtos-precos');
          const precosMap = {};
          response.data.forEach(p => {
            precosMap[p.id] = {
              quantidade_cx: p.quantidade_cx,
              preco_cx: p.preco_cx,
              preco_kg_un: p.preco_kg_un,
              precos_variacoes: p.precos_variacoes || {}  // Incluir preços por variação
            };
          });
          setPrecosFornecedorSalvos(precosMap);
        } catch (error) {
          console.error('Erro ao carregar preços do fornecedor:', error);
        }
      }
    };
    loadPrecosFornecedor();
  }, [usuario]);

  // Auto-preencher dados quando modal de proposta abre (apenas uma vez)
  useEffect(() => {
    if (showSubmitPrice && selectedCotacao && !precosInicializados && produtos.length > 0) {
      const precosIniciais = {};
      
      // Se o fornecedor já enviou proposta, carregar os dados enviados
      const fornecedorData = selectedCotacao.fornecedores?.[usuario.id];
      const propostaEnviada = fornecedorData?.produtos || {};
      
      selectedCotacao.produtos_ids.forEach(produtoId => {
        const produto = produtos.find(p => p.id === produtoId);
        const temVariacoes = produto?.variacoes && produto.variacoes.length > 0;
        
        // Se já tem proposta enviada para este produto, usar esses dados
        if (propostaEnviada[produtoId]) {
          if (temVariacoes) {
            // Carregar variações da proposta
            precosIniciais[produtoId] = {
              peso_cx: propostaEnviada[produtoId].peso_cx || '',
              variacoes: propostaEnviada[produtoId].variacoes || {}
            };
          } else {
            precosIniciais[produtoId] = {
              valor_caixa: propostaEnviada[produtoId].valor_caixa || '',
              quantidade_kg: propostaEnviada[produtoId].quantidade_kg || '',
              preco_kg_un: propostaEnviada[produtoId].preco_kg_un || ''
            };
          }
        } else {
          // Se não tem proposta, buscar dos preços salvos ou auto-preencher
          if (temVariacoes) {
            // Para produtos com variações, auto-preencher peso e preços por variação se existirem
            const precoSalvo = precosFornecedorSalvos[produtoId];
            const variacoesIniciais = {};
            
            // Carregar preços por variação se existirem nos preços salvos
            if (precoSalvo && precoSalvo.precos_variacoes) {
              produto.variacoes.forEach(variacao => {
                if (precoSalvo.precos_variacoes[variacao]) {
                  variacoesIniciais[variacao] = {
                    valor_caixa: precoSalvo.precos_variacoes[variacao]
                  };
                }
              });
            }
            
            precosIniciais[produtoId] = {
              peso_cx: precoSalvo?.quantidade_cx || produto.peso_cx || '',
              variacoes: variacoesIniciais
            };
          } else {
            // Lógica original para produtos sem variações
            const precoSalvo = precosFornecedorSalvos[produtoId];
            if (precoSalvo && precoSalvo.preco_cx) {
              precosIniciais[produtoId] = {
                valor_caixa: precoSalvo.preco_cx,
                quantidade_kg: precoSalvo.quantidade_cx || produto?.peso_cx,
                preco_kg_un: precoSalvo.preco_kg_un
              };
            } else if (produto && produto.unidade !== 'UN' && produto.peso_cx) {
              precosIniciais[produtoId] = {
                quantidade_kg: produto.peso_cx
              };
            }
          }
        }
      });
      
      if (Object.keys(precosIniciais).length > 0) {
        setPrecos(precosIniciais);
      }
      setPrecosInicializados(true);
    }
    
    // Reset flag quando modal fecha
    if (!showSubmitPrice) {
      setPrecosInicializados(false);
    }
  }, [showSubmitPrice, selectedCotacao, produtos, precosInicializados, usuario.id]);

  // Load fornecedores when component mounts (for COMPRADOR)
  React.useEffect(() => {
    if (temPermissaoComprador()) {
      loadFornecedores();
    }
  }, []);

  const loadFornecedores = async () => {
    try {
      const usuariosResponse = await axios.get('/usuarios');
      const fornecedoresList = usuariosResponse.data.filter(u => u.tipo_usuario === 'Fornecedor' && u.ativo);
      setFornecedores(fornecedoresList);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
    }
  };

  const getFornecedoresPendentes = (cotacao) => {
    if (!cotacao.produtos_ids || cotacao.produtos_ids.length === 0) {
      return [];
    }
    
    // Fornecedores que têm pelo menos um produto da cotação vinculado
    const fornecedoresVinculados = fornecedores.filter(forn => 
      forn.produtos_vinculados?.some(prodId => cotacao.produtos_ids.includes(prodId))
    );
    
    // Fornecedores que já enviaram proposta
    const fornecedoresQueEnviaram = Object.keys(cotacao.fornecedores || {})
      .filter(fornId => cotacao.fornecedores[fornId]?.enviado)
      .map(fornId => fornId);
    
    // Fornecedores pendentes = vinculados que não enviaram
    const pendentes = fornecedoresVinculados.filter(forn => 
      !fornecedoresQueEnviaram.includes(forn.id)
    );
    
    return pendentes;
  };


  // Load pedidos and produtos when form opens
  React.useEffect(() => {
    if (showForm && temPermissaoComprador()) {
      loadFormData();
    }
  }, [showForm]);

  // Load produtos when supplier opens proposal form
  React.useEffect(() => {
    if (showSubmitPrice && usuario.tipo_usuario === 'Fornecedor') {
      loadProdutosForSupplier();
    }
  }, [showSubmitPrice]);

  // Load produtos when buyer OR supplier opens visualization screen
  React.useEffect(() => {
    if (showCotacaoDetail && (temPermissaoComprador() || usuario.tipo_usuario === 'Fornecedor')) {
      loadProdutosForVisualization();
    }
  }, [showCotacaoDetail]);

  // Função utilitária para buscar todos os registros com paginação
  const fetchAllPaginated = async (endpoint, limit = 1000) => {
    let allItems = [];
    let skip = 0;
    let hasMore = true;
    
    while (hasMore) {
      const response = await axios.get(`${endpoint}?skip=${skip}&limit=${limit}`);
      const data = response.data;
      
      if (data.items) {
        allItems = [...allItems, ...data.items];
        hasMore = data.has_more || false;
        skip += limit;
      } else if (Array.isArray(data)) {
        allItems = data;
        hasMore = false;
      } else {
        allItems = [];
        hasMore = false;
      }
    }
    
    return allItems;
  };

  const loadProdutosForSupplier = async () => {
    try {
      const produtosData = await fetchAllPaginated('/produtos');
      setProdutos(produtosData);
    } catch (error) {
      toast({
        title: "Erro ao carregar produtos",
        description: error.response?.data?.detail || "Erro interno do servidor",
        variant: "destructive"
      });
    }
  };

  const loadProdutosForVisualization = async () => {
    try {
      const produtosData = await fetchAllPaginated('/produtos');
      setProdutos(produtosData);
      console.log('Produtos carregados para visualização:', produtosData.length, 'produtos');
    } catch (error) {
      console.error('Erro ao carregar produtos para visualização:', error);
      toast({
        title: "Erro ao carregar produtos",
        description: error.response?.data?.detail || "Erro interno do servidor",
        variant: "destructive"
      });
    }
  };

  const loadFormData = async () => {
    try {
      const [pedidosData, produtosData] = await Promise.all([
        fetchAllPaginated('/pedidos'),
        fetchAllPaginated('/produtos')
      ]);
      setPedidos(pedidosData);
      setProdutos(produtosData);
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description: error.response?.data?.detail || "Erro interno do servidor",
        variant: "destructive"
      });
    }
  };

  // Função para formatar data/hora em GMT-3 (Brasília)
  const formatarDataHoraBrasilia = (dataISO) => {
    const data = new Date(dataISO);
    
    // Converter para string no fuso de São Paulo
    const opcoes = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo',
      hour12: false
    };
    
    try {
      return new Intl.DateTimeFormat('pt-BR', opcoes).format(data);
    } catch (error) {
      // Fallback: conversão manual
      // Converter de UTC para GMT-3 (subtrair 3 horas)
      const dataLocal = new Date(data.getTime() - (3 * 60 * 60 * 1000));
      const dia = String(dataLocal.getUTCDate()).padStart(2, '0');
      const mes = String(dataLocal.getUTCMonth() + 1).padStart(2, '0');
      const ano = dataLocal.getUTCFullYear();
      const hora = String(dataLocal.getUTCHours()).padStart(2, '0');
      const minuto = String(dataLocal.getUTCMinutes()).padStart(2, '0');
      return `${dia}/${mes}/${ano}, ${hora}:${minuto}`;
    }
  };

  // Encerramento automático de cotações - atualização visual em tempo real
  React.useEffect(() => {
    const verificarEncerramentoAutomatico = async () => {
      const agora = new Date();
      let algumaCotacaoEncerrada = false;
      
      for (const cotacao of cotacoes) {
        if (cotacao.status_cotacao === 'Ativa') {
          const dataExpiracao = new Date(cotacao.expira_em);
          
          // Se a data de expiração já passou
          if (dataExpiracao <= agora) {
            // Apenas COMPRADOR pode chamar a API de encerramento
            if (temPermissaoComprador()) {
              try {
                // Verificar se há produtos com variações
                const produtosComVariacoes = produtos.filter(p => 
                  cotacao.produtos_ids.includes(p.id) && 
                  p.variacoes && 
                  p.variacoes.length > 0
                );
                
                if (produtosComVariacoes.length > 0) {
                  // TEM variações - não encerrar automaticamente
                  // O comprador precisa selecionar as variações manualmente
                  console.log(`Cotação ${cotacao.numero} expirou mas tem variações - aguardando seleção do comprador`);
                  
                  // Notificar apenas uma vez (usar algum controle para evitar spam)
                  if (!cotacao._notificacaoVariacoesEnviada) {
                    toast({
                      title: "⚠️ Ação necessária",
                      description: `A cotação ${cotacao.numero} expirou. Clique para selecionar as variações vencedoras.`,
                      duration: 10000
                    });
                    // Marcar que já foi notificado (apenas no estado local)
                    cotacao._notificacaoVariacoesEnviada = true;
                  }
                } else {
                  // NÃO tem variações - encerrar normalmente
                  await axios.put(`/cotacoes/${cotacao.id}/encerrar`);
                  console.log(`Cotação ${cotacao.numero} encerrada automaticamente`);
                  algumaCotacaoEncerrada = true;
                  
                  // Notificar o usuário
                  toast({
                    title: "Cotação encerrada automaticamente",
                    description: `A cotação ${cotacao.numero} atingiu seu prazo e foi encerrada.`
                  });
                }
              } catch (error) {
                console.error(`Erro ao encerrar cotação ${cotacao.numero}:`, error);
              }
            } else {
              // Para outros usuários, apenas marcar que precisa recarregar
              algumaCotacaoEncerrada = true;
            }
          }
        }
      }
      
      // Recarregar dados se alguma cotação foi encerrada
      if (algumaCotacaoEncerrada) {
        onRefresh();
      }
    };
    
    // Verificar a cada 15 segundos (mais frequente para melhor UX)
    const intervalo = setInterval(verificarEncerramentoAutomatico, 15000);
    
    // Verificar imediatamente ao carregar
    verificarEncerramentoAutomatico();
    
    return () => clearInterval(intervalo);
  }, [cotacoes, usuario.tipo_usuario, onRefresh, toast, produtos]);

  const handleCreateCotacao = async () => {
    try {
      // Validar nome
      if (!formData.nome || !formData.nome.trim()) {
        toast({
          title: "Nome obrigatório",
          description: "Por favor, insira um nome para a cotação",
          variant: "destructive"
        });
        return;
      }

      // Validar data e hora
      if (!formData.data_encerramento || !formData.hora_encerramento) {
        toast({
          title: "Erro",
          description: "Por favor, defina a data e horário de encerramento",
          variant: "destructive"
        });
        return;
      }
      
      // Usuário digita hora de Brasília (GMT-3), precisamos converter para UTC
      const [ano, mes, dia] = formData.data_encerramento.split('-');
      const [hora, minuto] = formData.hora_encerramento.split(':');
      
      // Criar timestamp de Brasília (interpretando como horário local de Brasília)
      // e converter para UTC adicionando 3 horas
      const dataHoraBrasiliaUTC = Date.UTC(
        parseInt(ano), 
        parseInt(mes) - 1, 
        parseInt(dia), 
        parseInt(hora) + 3,  // Adicionar 3h para converter Brasília → UTC
        parseInt(minuto)
      );
      
      const dataHoraUTC = new Date(dataHoraBrasiliaUTC);
      
      // Verificar se a data/hora é no futuro
      // Comparar em UTC para evitar problemas de timezone
      const agoraUTC = Date.now();
      
      if (dataHoraBrasiliaUTC <= agoraUTC) {
        toast({
          title: "Erro",
          description: "A data/hora de encerramento deve ser no futuro",
          variant: "destructive"
        });
        return;
      }
      
      const payload = {
        ...formData,
        expira_em: dataHoraUTC.toISOString()
      };
      
      if (editingCotacao) {
        // Update existing cotação
        await axios.put(`/cotacoes/${editingCotacao.id}`, payload);
        toast({ title: "Cotação atualizada com sucesso!" });
      } else {
        // Create new cotação
        await axios.post('/cotacoes', payload);
        toast({ title: "Cotação criada com sucesso!" });
      }
      
      setShowForm(false);
      setEditingCotacao(null);
      setFormData({
        nome: '',
        data_encerramento: '',
        hora_encerramento: '',
        baseada_em_pedidos: false,
        pedidos_ids: []
      });
      onRefresh();
    } catch (error) {
      toast({
        title: editingCotacao ? "Erro ao atualizar cotação" : "Erro ao criar cotação",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  const handleViewCotacao = (cotacao) => {
    setSelectedCotacao(cotacao);
    setShowCotacaoDetail(true);
  };

  const handleSubmitPrices = async () => {
    try {
      // Filtrar e processar produtos baseado no tipo (UN vs CX/SC) e variações
      const precosProcessados = {};
      
      for (const [produtoId, preco] of Object.entries(precos)) {
        const produto = produtos.find(p => p.id === produtoId);
        if (!produto) continue;
        
        const temVariacoes = produto.variacoes && produto.variacoes.length > 0;
        
        if (produto.unidade === 'UN') {
          // Produto UN: verificar preco_kg_un OU variações
          const temVariacoesUN = produto.variacoes && produto.variacoes.length > 0;
          
          if (temVariacoesUN) {
            // PRODUTO UN COM VARIAÇÕES - processar cada variação
            const variacoes = preco.variacoes || {};
            
            const variacoesPreenchidas = Object.entries(variacoes).filter(([nome, data]) => {
              const valor = data.valor_caixa?.toString().trim();
              return valor && parseFloat(valor) > 0;
            });
            
            if (variacoesPreenchidas.length > 0) {
              // Processar variações para produto UN
              const variacoesProcessadas = {};
              for (const [nomeVariacao, data] of variacoesPreenchidas) {
                const valorNumerico = parseFloat(data.valor_caixa) || 0;
                if (valorNumerico <= 0) {
                  toast({
                    title: "Valor inválido",
                    description: `${produto.nome} - ${nomeVariacao}: valor deve ser maior que zero`,
                    variant: "destructive"
                  });
                  return;
                }
                variacoesProcessadas[nomeVariacao] = {
                  valor_caixa: valorNumerico
                };
              }
              
              precosProcessados[produtoId] = {
                isUN: true,
                variacoes: variacoesProcessadas
              };
            }
          } else {
            // PRODUTO UN SEM VARIAÇÕES - lógica original
            const precoKgUn = preco.preco_kg_un?.toString().trim();
            if (precoKgUn) {
              const precoNumerico = parseFloat(precoKgUn) || 0;
              if (precoNumerico <= 0) {
                toast({
                  title: "Valor inválido",
                  description: `Produto ${produto.nome} (UN): preço deve ser maior que zero`,
                  variant: "destructive"
                });
                return;
              }
              precosProcessados[produtoId] = {
                preco_kg_un: precoNumerico
              };
            }
          }
        } else if (temVariacoes) {
          // PRODUTOS COM VARIAÇÕES
          const pesoCaixa = preco.peso_cx?.toString().trim();
          const variacoes = preco.variacoes || {};
          
          // Verificar se pelo menos uma variação foi preenchida
          const variacoesPreenchidas = Object.entries(variacoes).filter(([nome, data]) => {
            const valor = data.valor_caixa?.toString().trim();
            return valor && parseFloat(valor) > 0;
          });
          
          if (variacoesPreenchidas.length > 0) {
            // Validar peso (obrigatório se tiver variações preenchidas)
            const pesoNumerico = parseFloat(pesoCaixa) || 0;
            if (pesoNumerico <= 0) {
              toast({
                title: "Peso obrigatório",
                description: `Produto ${produto.nome}: preencha o PESO CX (compartilhado entre variações)`,
                variant: "destructive"
              });
              return;
            }
            
            // Processar variações
            const variacoesProcessadas = {};
            for (const [nomeVariacao, data] of variacoesPreenchidas) {
              const valorNumerico = parseFloat(data.valor_caixa) || 0;
              if (valorNumerico <= 0) {
                toast({
                  title: "Valor inválido",
                  description: `${produto.nome} - ${nomeVariacao}: valor deve ser maior que zero`,
                  variant: "destructive"
                });
                return;
              }
              variacoesProcessadas[nomeVariacao] = {
                valor_caixa: valorNumerico
              };
            }
            
            precosProcessados[produtoId] = {
              peso_cx: pesoNumerico,
              variacoes: variacoesProcessadas
            };
          }
        } else {
          // Produto CX/SC SEM VARIAÇÕES: lógica original
          const valorCaixa = preco.valor_caixa?.toString().trim();
          const pesoCaixa = preco.quantidade_kg?.toString().trim();
          
          if (valorCaixa && valorCaixa !== '') {
            const valorNumerico = parseFloat(valorCaixa) || 0;
            const pesoNumerico = parseFloat(pesoCaixa) || 0;
            
            if (valorNumerico <= 0) {
              toast({
                title: "Valor inválido",
                description: `Produto ${produto.nome} (${produto.unidade}): VALOR CX deve ser maior que zero`,
                variant: "destructive"
              });
              return;
            }
            
            if (pesoNumerico <= 0) {
              toast({
                title: "Peso obrigatório",
                description: `Produto ${produto.nome} (${produto.unidade}): preencha o PESO CX também`,
                variant: "destructive"
              });
              return;
            }
            
            precosProcessados[produtoId] = {
              valor_caixa: valorNumerico,
              quantidade_kg: pesoNumerico
            };
          }
        }
      }
      
      // Permitir envio mesmo sem produtos (indica que fornecedor não tem nenhum disponível)
      await axios.post(`/cotacoes/${selectedCotacao.id}/submit-price`, precosProcessados);
      
      const numProdutos = Object.keys(precosProcessados).length;
      toast({ 
        title: "Proposta enviada com sucesso!",
        description: numProdutos > 0 
          ? `${numProdutos} produto(s) registrado(s) na cotação`
          : "Proposta registrada (nenhum produto disponível no momento)"
      });
      setShowSubmitPrice(false);
      setSelectedCotacao(null);
      setPrecos({});
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao enviar proposta",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  const getTimeRemaining = (expiraEm) => {
    const now = new Date();
    const expira = new Date(expiraEm);
    const diff = expira - now;
    
    if (diff <= 0) return "Expirada";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} dia(s)`;
    }
    return `${hours}h ${minutes}m`;
  };

  const handleEditCotacao = (cotacao) => {
    // Implement edit functionality - for now, redirect to create form with pre-filled data
    setFormData({
      durabilidade_horas: cotacao.durabilidade_horas,
      baseada_em_pedidos: cotacao.baseada_em_pedidos,
      pedidos_ids: cotacao.pedidos_ids || []
    });
    setEditingCotacao(cotacao);
    setShowForm(true);
  };

  // handleEnviarCotacao function removed - suppliers now access quotations directly through the app

  const encerrarCotacao = async (cotacao) => {
    // Verificar se há produtos com variações na cotação
    const produtosComVariacoes = produtos.filter(p => 
      cotacao.produtos_ids.includes(p.id) && 
      p.variacoes && 
      p.variacoes.length > 0
    );
    
    if (produtosComVariacoes.length > 0) {
      // Tem variações - abrir modal de seleção
      setCotacaoParaEncerrar(cotacao);
      
      // Inicializar variações selecionadas (primeira variação de cada produto por padrão)
      const variacoesInicial = {};
      produtosComVariacoes.forEach(produto => {
        if (produto.variacoes && produto.variacoes.length > 0) {
          variacoesInicial[produto.id] = produto.variacoes[0]; // Primeira variação por padrão
        }
      });
      setVariacoesSelecionadas(variacoesInicial);
      setShowModalVariacoes(true);
    } else {
      // Não tem variações - encerrar diretamente
      const confirmed = await showConfirmDialog(
        "Encerrar Cotação",
        `Tem certeza que deseja encerrar a cotação ${cotacao.numero}?`
      );
      
      if (!confirmed) return;

      try {
        await axios.put(`/cotacoes/${cotacao.id}/encerrar`);
        toast({ title: "Cotação encerrada com sucesso!" });
        onRefresh();
      } catch (error) {
        toast({
          title: "Erro ao encerrar cotação",
          description: error.response?.data?.detail || "Erro interno",
          variant: "destructive"
        });
      }
    }
  };
  
  // Função para confirmar encerramento após seleção de variações
  const confirmarEncerramentoComVariacoes = async () => {
    const cotacao = cotacaoParaEncerrar;
    
    const confirmed = await showConfirmDialog(
      "Encerrar Cotação",
      `Tem certeza que deseja encerrar a cotação ${cotacao.numero} com as variações selecionadas?`
    );
    
    if (!confirmed) return;

    try {
      await axios.put(`/cotacoes/${cotacao.id}/encerrar`, {
        variacoes_vencedoras: variacoesSelecionadas
      });
      toast({ title: "Cotação encerrada com sucesso!" });
      setShowModalVariacoes(false);
      setCotacaoParaEncerrar(null);
      setVariacoesSelecionadas({});
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao encerrar cotação",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  const handleEditarVencedor = async (cotacao, produto) => {
    console.log('handleEditarVencedor chamado:', { cotacao, produto });
    
    if (!produto || !produto.id) {
      console.error('Produto inválido:', produto);
      toast({
        title: "Erro",
        description: "Produto não encontrado. Recarregue a página e tente novamente.",
        variant: "destructive"
      });
      return;
    }
    
    setCotacaoEditando(cotacao);
    setProdutoEditando(produto);
    
    // Pegar vencedor atual se existir
    const vencedorAtual = cotacao.vencedores?.[produto.id] || {};
    console.log('Vencedor atual:', vencedorAtual);
    
    setVencedorEditando({
      fornecedor_id: vencedorAtual.fornecedor_id || '',
      nome_fornecedor: vencedorAtual.nome_fornecedor || '',
      valor_caixa: vencedorAtual.valor_caixa || 0,
      quantidade_kg: vencedorAtual.quantidade_kg || 0
    });
    
    // Buscar fornecedores vinculados a este produto
    try {
      console.log('Buscando fornecedores para produto:', produto.id);
      const response = await axios.get(`/produtos/${produto.id}/fornecedores`);
      console.log('Fornecedores encontrados:', response.data);
      setFornecedoresVinculados(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
      toast({
        title: "Erro ao carregar fornecedores",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
      setFornecedoresVinculados([]);
    }
    
    console.log('Abrindo modal de edição');
    setShowEditarVencedorModal(true);
    console.log('showEditarVencedorModal definido como true');
    console.log('Estados:', { 
      showEditarVencedorModal: true, 
      produtoEditando: produto.nome, 
      cotacaoEditando: cotacao.numero 
    });
  };

  const handleSalvarVencedor = async () => {
    if (!cotacaoEditando || !produtoEditando) return;
    
    // Validações
    if (!vencedorEditando.fornecedor_id || !vencedorEditando.nome_fornecedor) {
      toast({
        title: "Erro",
        description: "Selecione um fornecedor",
        variant: "destructive"
      });
      return;
    }
    
    if (vencedorEditando.valor_caixa <= 0) {
      toast({
        title: "Erro",
        description: "Valor da caixa deve ser maior que zero",
        variant: "destructive"
      });
      return;
    }
    
    // Para produtos UN, não validar peso da caixa (será sempre 1 ou valor especial)
    const produtoInfo = produtos.find(p => p.id === produtoEditando.id);
    const isUN = produtoInfo && produtoInfo.unidade === 'UN';
    
    if (!isUN && vencedorEditando.quantidade_kg <= 0) {
      toast({
        title: "Erro",
        description: "Peso da caixa deve ser maior que zero",
        variant: "destructive"
      });
      return;
    }
    
    // Para produtos UN, usar preco_kg_un ao invés de quantidade_kg
    if (isUN && vencedorEditando.quantidade_kg <= 0) {
      vencedorEditando.quantidade_kg = 1; // Valor padrão para UN
    }
    
    try {
      // Preparar vencedores atualizados
      const vencedoresAtualizados = {
        ...cotacaoEditando.vencedores,
        [produtoEditando.id]: {
          fornecedor_id: vencedorEditando.fornecedor_id,
          nome_fornecedor: vencedorEditando.nome_fornecedor,
          valor_caixa: parseFloat(vencedorEditando.valor_caixa),
          quantidade_kg: parseFloat(vencedorEditando.quantidade_kg),
          ...(isUN && { preco_kg_un: parseFloat(vencedorEditando.valor_caixa) }) // Para produtos UN
        }
      };
      
      // Comprador/Administrador pode editar vencedores em qualquer status
      await axios.put(`/cotacoes/${cotacaoEditando.id}/vencedores`, vencedoresAtualizados);
      
      toast({
        title: "Vencedor atualizado",
        description: "As alterações foram salvas com sucesso"
      });
      
      setShowEditarVencedorModal(false);
      
      // Recarregar apenas a cotação atual (mantendo na tela)
      if (cotacaoEditando) {
        await recarregarCotacaoAtual(cotacaoEditando.id);
      }
    } catch (error) {
      toast({
        title: "Erro ao salvar",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  const handleChangeFornecedorVencedor = (fornecedorId) => {
    const fornecedor = fornecedoresVinculados.find(f => f.id === fornecedorId);
    if (fornecedor) {
      setVencedorEditando({
        ...vencedorEditando,
        fornecedor_id: fornecedor.id,
        nome_fornecedor: fornecedor.nome_completo
      });
    }
  };

  // Função para adicionar vencedor manualmente (produtos SEM propostas)
  const handleAdicionarVencedorManual = async (cotacao, produto) => {
    setCotacaoEditando(cotacao);
    setProdutoEditando(produto);
    
    // Verificar se já existe vencedor definido (pode ter sido adicionado manualmente antes)
    const vencedorExistente = cotacao.vencedores?.[produto.id] || {};
    
    // Resetar dados do modal
    setVencedorManualDados({
      fornecedor_id: vencedorExistente.fornecedor_id || '',
      fornecedor_nome: vencedorExistente.nome_fornecedor || '',
      valor_caixa: vencedorExistente.valor_caixa || 0,
      quantidade_kg: vencedorExistente.quantidade_kg || produto.peso_cx || 0,
      variacao: vencedorExistente.variacao || ''
    });
    
    // Carregar todos os fornecedores para seleção
    try {
      const response = await axios.get('/usuarios');
      const fornecedores = response.data.filter(u => u.tipo_usuario === 'Fornecedor');
      setTodosFornecedores(fornecedores);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
      setTodosFornecedores([]);
    }
    
    setShowAdicionarVencedorManualModal(true);
  };
  
  const handleSalvarVencedorManual = async () => {
    if (!cotacaoEditando || !produtoEditando) return;
    
    // Validações
    if (!vencedorManualDados.fornecedor_nome.trim()) {
      toast({
        title: "Erro",
        description: "Nome do fornecedor é obrigatório",
        variant: "destructive"
      });
      return;
    }
    
    if (!vencedorManualDados.valor_caixa || vencedorManualDados.valor_caixa <= 0) {
      toast({
        title: "Erro",
        description: "Valor da caixa deve ser maior que zero",
        variant: "destructive"
      });
      return;
    }
    
    const produtoInfo = produtos.find(p => p.id === produtoEditando.id);
    const isUN = produtoInfo && produtoInfo.unidade === 'UN';
    const temVariacoes = produtoInfo && produtoInfo.variacoes && produtoInfo.variacoes.length > 0;
    
    if (!isUN && (!vencedorManualDados.quantidade_kg || vencedorManualDados.quantidade_kg <= 0)) {
      toast({
        title: "Erro",
        description: "Peso da caixa deve ser maior que zero",
        variant: "destructive"
      });
      return;
    }
    
    // Validar variação se produto tiver variações
    if (temVariacoes && !vencedorManualDados.variacao) {
      toast({
        title: "Erro",
        description: "Selecione uma variação para este produto",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const dadosEnvio = {
        fornecedor_id: vencedorManualDados.fornecedor_id || 'manual',
        fornecedor_nome: vencedorManualDados.fornecedor_nome.trim(),
        valor_caixa: parseFloat(vencedorManualDados.valor_caixa),
        quantidade_kg: isUN ? 1 : parseFloat(vencedorManualDados.quantidade_kg),
        variacao: temVariacoes ? vencedorManualDados.variacao : null
      };
      
      await axios.post(
        `/cotacoes/${cotacaoEditando.id}/produtos/${produtoEditando.id}/vencedor-manual`,
        dadosEnvio
      );
      
      toast({
        title: "Vencedor adicionado!",
        description: `${vencedorManualDados.fornecedor_nome} foi definido como vencedor de ${produtoEditando.nome}`
      });
      
      setShowAdicionarVencedorManualModal(false);
      
      // Recarregar apenas a cotação atual (mantendo na tela)
      if (cotacaoEditando) {
        await recarregarCotacaoAtual(cotacaoEditando.id);
      }
    } catch (error) {
      toast({
        title: "Erro ao salvar",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  // ===== FUNÇÕES DE UPLOAD NF-e (XML/PDF) =====
  
  const handleUploadNfeClick = () => {
    if (nfeFileInputRef.current) {
      nfeFileInputRef.current.click();
    }
  };
  
  const handleNfeFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const fileName = file.name.toLowerCase();
    const isXml = fileName.endsWith('.xml');
    const isPdf = fileName.endsWith('.pdf');
    
    if (!isXml && !isPdf) {
      toast({
        title: "Arquivo inválido",
        description: "Por favor, selecione um arquivo XML ou PDF de NF-e",
        variant: "destructive"
      });
      return;
    }
    
    setNfeUploading(true);
    
    if (isPdf) {
      toast({
        title: "Processando PDF...",
        description: "O OCR pode levar alguns segundos. Aguarde..."
      });
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `/cotacoes/${selectedCotacao.id}/upload-nfe`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setNfeData(response.data);
      
      const matchesIniciais = [];
      const produtosCotacao = response.data.cotacao.produtos;
      const produtosNfe = response.data.nfe.produtos;
      
      produtosNfe.forEach((prodNfe, index) => {
        let melhorMatch = null;
        let melhorScore = 0;
        
        produtosCotacao.forEach(prodCot => {
          const nomeCotLower = prodCot.nome.toLowerCase();
          const nomeNfeLower = prodNfe.nome.toLowerCase();
          
          if (nomeCotLower.includes(nomeNfeLower) || nomeNfeLower.includes(nomeCotLower)) {
            melhorMatch = prodCot;
            melhorScore = 100;
          } else {
            const palavrasCot = nomeCotLower.split(/\s+/);
            const palavrasNfe = nomeNfeLower.split(/\s+/);
            let matches = 0;
            palavrasCot.forEach(p => {
              if (palavrasNfe.some(pn => pn.includes(p) || p.includes(pn))) {
                matches++;
              }
            });
            const score = (matches / Math.max(palavrasCot.length, palavrasNfe.length)) * 100;
            if (score > melhorScore && score > 30) {
              melhorScore = score;
              melhorMatch = prodCot;
            }
          }
        });
        
        matchesIniciais.push({
          nfe_index: index,
          produto_nfe: prodNfe,
          produto_cotacao_id: melhorMatch ? melhorMatch.id : '',
          quantidade_kg: prodNfe.quantidade || '',
          variacao: '',
          sobrescrever: false,
          auto_matched: !!melhorMatch
        });
      });
      
      setNfeMatches(matchesIniciais);
      setShowUploadNfeModal(true);
      
      const sourceType = response.data.source === 'pdf_ocr' ? 'PDF (via OCR)' : 'XML';
      toast({
        title: `${sourceType} carregado com sucesso!`,
        description: `${produtosNfe.length} produto(s) encontrado(s) na NF-e de ${response.data.nfe.fornecedor.nome_exibicao || 'fornecedor'}`
      });
      
    } catch (error) {
      toast({
        title: "Erro ao processar arquivo",
        description: error.response?.data?.detail || "Erro ao fazer upload do arquivo",
        variant: "destructive"
      });
    } finally {
      setNfeUploading(false);
      if (nfeFileInputRef.current) {
        nfeFileInputRef.current.value = '';
      }
    }
  };
  
  const handleNfeMatchChange = (nfeIndex, field, value) => {
    setNfeMatches(prev => prev.map((match, idx) => {
      if (idx === nfeIndex) {
        return { ...match, [field]: value, auto_matched: false };
      }
      return match;
    }));
  };
  
  const handleAplicarNfe = async () => {
    if (!nfeData || !selectedCotacao) return;
    
    const matchesValidos = nfeMatches.filter(m => m.produto_cotacao_id);
    
    if (matchesValidos.length === 0) {
      toast({
        title: "Nenhum match selecionado",
        description: "Selecione pelo menos um produto da cotação para vincular",
        variant: "destructive"
      });
      return;
    }
    
    setNfeApplying(true);
    
    try {
      const dadosEnvio = {
        fornecedor_nome: nfeData.nfe.fornecedor.nome_exibicao || nfeData.nfe.fornecedor.nome,
        fornecedor_cnpj: nfeData.nfe.fornecedor.cnpj,
        matches: matchesValidos.map(m => ({
          produto_cotacao_id: m.produto_cotacao_id,
          produto_nfe: {
            nome: m.produto_nfe.nome,
            valor_unitario: m.produto_nfe.valor_unitario,
            quantidade: m.produto_nfe.quantidade
          },
          quantidade_kg: parseFloat(m.quantidade_kg) || null,
          variacao: m.variacao || null,
          sobrescrever: m.sobrescrever
        }))
      };
      
      const response = await axios.post(
        `/cotacoes/${selectedCotacao.id}/aplicar-nfe`,
        dadosEnvio
      );
      
      toast({
        title: "NF-e aplicada com sucesso!",
        description: response.data.message
      });
      
      setShowUploadNfeModal(false);
      setNfeData(null);
      setNfeMatches([]);
      
      await recarregarCotacaoAtual(selectedCotacao.id);
      
    } catch (error) {
      toast({
        title: "Erro ao aplicar NF-e",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setNfeApplying(false);
    }
  };

  const handleDeleteCotacao = async (cotacao) => {
    const confirmed = await showConfirmDialog(
      "Excluir Cotação",
      "Tem certeza que deseja excluir?"
    );
    
    if (!confirmed) return;

    try {
      await axios.delete(`/cotacoes/${cotacao.id}`);
      toast({ 
        title: "Cotação excluída com sucesso!",
        description: `A cotação ${cotacao.numero} foi removida permanentemente.` 
      });
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao excluir cotação",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  if (showForm && temPermissaoComprador()) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => setShowForm(false)}
            className="text-green-700 hover:text-green-800 font-medium"
            data-testid="back-from-cotacao-form"
          >
            ← Voltar
          </button>
        </div>
        
        <h3 className="text-lg font-medium mb-6">
          {editingCotacao ? `Editar Cotação ${editingCotacao.numero}` : 'Criar Nova Cotação'}
        </h3>
        
        <form onSubmit={(e) => { e.preventDefault(); handleCreateCotacao(); }} className="space-y-6 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome da Cotação *
            </label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
              placeholder="Ex: Cotação Frutas Fevereiro 2025"
              required
              data-testid="cotacao-nome-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Dê um nome descritivo para identificar facilmente esta cotação.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>📅 Controle de Tempo da Cotação</strong>
              <br />
              Defina a data e horário exatos em que esta cotação será encerrada automaticamente.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Encerramento *
              </label>
              <input
                type="date"
                value={formData.data_encerramento}
                onChange={(e) => setFormData({ ...formData, data_encerramento: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                required
                min={new Date().toISOString().split('T')[0]}
                data-testid="cotacao-data-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Horário de Encerramento *
              </label>
              <input
                type="time"
                value={formData.hora_encerramento}
                onChange={(e) => setFormData({ ...formData, hora_encerramento: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                required
                data-testid="cotacao-hora-input"
              />
            </div>
          </div>
          
          <p className="text-sm text-gray-500">
            ⏰ A cotação será encerrada automaticamente no horário definido (horário de Brasília - GMT-3)
          </p>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.baseada_em_pedidos}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  baseada_em_pedidos: e.target.checked,
                  pedidos_ids: e.target.checked ? formData.pedidos_ids : []
                })}
                className="mr-2"
                data-testid="baseada-em-pedidos-checkbox"
              />
              <span className="text-sm text-gray-700">Baseada em pedidos existentes</span>
            </label>
            <p className="text-xs text-gray-500 ml-6 mt-1">
              Se marcada, a cotação incluirá apenas produtos dos pedidos selecionados. 
              Senão, incluirá todos os produtos do sistema.
            </p>
          </div>

          {formData.baseada_em_pedidos && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selecionar Pedidos ({pedidos.length} disponíveis)
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                {pedidos.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Nenhum pedido encontrado</p>
                ) : (
                  <div className="space-y-2">
                    {pedidos.map((pedido) => (
                      <label
                        key={pedido.id}
                        className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                        data-testid={`pedido-checkbox-${pedido.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.pedidos_ids.includes(pedido.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                pedidos_ids: [...formData.pedidos_ids, pedido.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                pedidos_ids: formData.pedidos_ids.filter(id => id !== pedido.id)
                              });
                            }
                          }}
                          className="mr-3"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">Pedido #{pedido.numero}</div>
                          <div className="text-sm text-gray-500">
                            {pedido.filial} • {pedido.produtos?.length || 0} produto(s) • 
                            {pedido.finalizado ? ' Finalizado' : ' Aberto'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2 text-sm text-gray-600">
                {formData.pedidos_ids.length} pedido(s) selecionado(s)
              </div>
            </div>
          )}
          
          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={formData.baseada_em_pedidos && formData.pedidos_ids.length === 0}
              className="px-6 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="create-cotacao-button"
            >
              {editingCotacao ? 'Atualizar Cotação' : 'Criar Cotação'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingCotacao(null);
                setFormData({
                  durabilidade_horas: 24,
                  baseada_em_pedidos: false,
                  pedidos_ids: []
                });
              }}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              data-testid="cancel-cotacao-button"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (showSubmitPrice && selectedCotacao && usuario.tipo_usuario === 'Fornecedor') {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => {
              setShowSubmitPrice(false);
              setSelectedCotacao(null);
              setPrecos({});
            }}
            className="text-green-700 hover:text-green-800 font-medium"
            data-testid="back-from-submit-price"
          >
            ← Voltar
          </button>
        </div>
        
        <h3 className="text-lg font-medium mb-6">
          Enviar Proposta - {selectedCotacao.numero}
        </h3>
        
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2">Informações da Cotação</h4>
          <div className="text-sm text-blue-700 space-y-1">
            <p>Expira em: {getTimeRemaining(selectedCotacao.expira_em)}</p>
            <p>Produtos disponíveis para você: {selectedCotacao.produtos_ids?.filter(id => 
              usuario.produtos_vinculados?.includes(id)).length || 0}</p>
          </div>
        </div>
        
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h5 className="font-medium text-yellow-800 mb-2">📝 Como preencher sua proposta:</h5>
          <div className="text-sm text-yellow-700 space-y-1">
            <p>• <strong>Produtos CX/SC:</strong> Informe VALOR CX e PESO CX (o peso pode vir preenchido do cadastro)</p>
            <p>• <strong>Produtos UN:</strong> Informe apenas o PREÇO KG/UN (preço final, sem cálculos)</p>
            <p>• <strong>Importante:</strong> Se você não preencher os campos de um produto, o sistema entenderá que você não tem esse item disponível</p>
          </div>
        </div>
        
        <div className="space-y-4">
          {selectedCotacao.produtos_ids
            ?.filter(produtoId => usuario.produtos_vinculados?.includes(produtoId))
            .map((produtoId) => {
              const produto = produtos.find(p => p.id === produtoId);
              if (!produto) {
                // Show loading state or product ID if product not loaded yet
                return (
                  <div key={produtoId} className="bg-gray-50 rounded-lg p-4 border">
                    <h4 className="font-medium text-gray-900 mb-3">
                      Carregando produto... (ID: {produtoId.substring(0, 8)}...)
                    </h4>
                    <div className="text-sm text-gray-500">
                      Aguarde enquanto carregamos os detalhes do produto
                    </div>
                  </div>
                );
              }
              
              return (
                <div key={produtoId} className="bg-white rounded-lg p-4 border-2 border-green-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900 text-lg">{produto.nome}</h4>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        Produto Vinculado
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {produto.unidade}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 mb-3">
                    <p><strong>Unidade:</strong> {produto.unidade}</p>
                    {produto.qualidade && (
                      <p><strong>Qualidade:</strong> {produto.qualidade}</p>
                    )}
                    {produto.peso_cx && produto.unidade !== 'UN' && (
                      <p className="text-blue-600"><strong>Peso CX cadastrado:</strong> {produto.peso_cx} kg</p>
                    )}
                  </div>
                  
                  {/* Campos diferentes para UN vs CX/SC */}
                  {produto.unidade === 'UN' ? (
                    // Produto UN: PREÇO KG/UN (com suporte a variações)
                    (() => {
                      const temVariacoesUN = produto.variacoes && produto.variacoes.length > 0;
                      
                      if (temVariacoesUN) {
                        // PRODUTO UN COM VARIAÇÕES
                        return (
                          <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700">
                              Preços por Variação (R$/un)
                            </label>
                            {produto.variacoes.map((variacao, idx) => (
                              <div key={idx} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="flex-1">
                                  <label className="block text-sm font-medium text-purple-700 mb-1">
                                    {variacao}
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500">R$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={precos[produtoId]?.variacoes?.[variacao]?.valor_caixa || ''}
                                      onChange={(e) => {
                                        const inputValue = e.target.value;
                                        const valorUN = inputValue === '' ? '' : inputValue;
                                        setPrecos({
                                          ...precos,
                                          [produtoId]: {
                                            ...precos[produtoId],
                                            variacoes: {
                                              ...(precos[produtoId]?.variacoes || {}),
                                              [variacao]: { valor_caixa: valorUN }
                                            }
                                          }
                                        });
                                      }}
                                      className="flex-1 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                                      placeholder="0.00"
                                      data-testid={`valor-variacao-un-${produtoId}-${idx}`}
                                    />
                                    <span className="text-sm text-gray-500">/un</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                            <p className="text-xs text-gray-500 mt-1">
                              Informe o preço por unidade para cada variação
                            </p>
                          </div>
                        );
                      } else {
                        // PRODUTO UN SEM VARIAÇÕES
                        return (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              PREÇO KG/UN (R$) - Preço final
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={precos[produtoId]?.preco_kg_un || ''}
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                setPrecos({
                                  ...precos,
                                  [produtoId]: {
                                    ...precos[produtoId],
                                    preco_kg_un: inputValue
                                  }
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                              placeholder="Ex: 12.50"
                              data-testid={`preco-kg-un-${produtoId}`}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Informe o preço final por kg ou por unidade (sem cálculos adicionais)
                            </p>
                          </div>
                        );
                      }
                    })()
                  ) : (
                    // Produto CX/SC
                    (() => {
                      const temVariacoes = produto.variacoes && produto.variacoes.length > 0;
                      
                      if (temVariacoes) {
                        // PRODUTOS COM VARIAÇÕES
                        return (
                          <div className="space-y-4">
                            {/* PESO CX - Único e compartilhado */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                PESO CX (kg) - Compartilhado entre todas as variações
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={precos[produtoId]?.peso_cx || ''}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const pesoCaixa = inputValue === '' ? '' : inputValue;
                                  setPrecos({
                                    ...precos,
                                    [produtoId]: {
                                      ...precos[produtoId],
                                      peso_cx: pesoCaixa
                                    }
                                  });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                                placeholder="Ex: 18.50"
                                data-testid={`peso-cx-${produtoId}`}
                              />
                              {produto.peso_cx && (
                                <p className="text-xs text-blue-600 mt-1">
                                  ℹ️ Auto-preenchido do cadastro. Pode editar se necessário.
                                </p>
                              )}
                            </div>
                            
                            {/* VARIAÇÕES - Um campo VALOR CX para cada */}
                            <div className="space-y-3">
                              <label className="block text-sm font-medium text-gray-700">
                                Valores por Variação
                              </label>
                              {produto.variacoes.map((variacao, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-600 mb-1">
                                      {variacao}
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-500">R$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={precos[produtoId]?.variacoes?.[variacao]?.valor_caixa || ''}
                                        onChange={(e) => {
                                          const inputValue = e.target.value;
                                          const valorCaixa = inputValue === '' ? '' : inputValue;
                                          setPrecos({
                                            ...precos,
                                            [produtoId]: {
                                              ...precos[produtoId],
                                              variacoes: {
                                                ...(precos[produtoId]?.variacoes || {}),
                                                [variacao]: { valor_caixa: valorCaixa }
                                              }
                                            }
                                          });
                                        }}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                                        placeholder="0.00"
                                        data-testid={`valor-variacao-${produtoId}-${idx}`}
                                      />
                                    </div>
                                  </div>
                                  {/* Mostrar preço/kg se tiver peso preenchido */}
                                  {(() => {
                                    const valorCaixa = parseFloat(precos[produtoId]?.variacoes?.[variacao]?.valor_caixa) || 0;
                                    const pesoCaixa = parseFloat(precos[produtoId]?.peso_cx) || 0;
                                    if (valorCaixa > 0 && pesoCaixa > 0) {
                                      const valorPorKg = valorCaixa / pesoCaixa;
                                      return (
                                        <div className="text-sm text-gray-600">
                                          = <span className="font-bold text-green-700">R$ {valorPorKg.toFixed(2)}/kg</span>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      } else {
                        // PRODUTOS SEM VARIAÇÕES - Lógica original
                        return (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                VALOR CX (R$)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={precos[produtoId]?.valor_caixa || ''}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const valorCaixa = inputValue === '' ? '' : inputValue;
                                  setPrecos({
                                    ...precos,
                                    [produtoId]: {
                                      ...precos[produtoId],
                                      valor_caixa: valorCaixa
                                    }
                                  });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                                placeholder="0.00"
                                data-testid={`valor-caixa-${produtoId}`}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                PESO CX (kg)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={precos[produtoId]?.quantidade_kg || ''}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const pesoCaixa = inputValue === '' ? '' : inputValue;
                                  setPrecos({
                                    ...precos,
                                    [produtoId]: {
                                      ...precos[produtoId],
                                      quantidade_kg: pesoCaixa
                                    }
                                  });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                                placeholder="Ex: 18.50"
                                data-testid={`quantidade-kg-${produtoId}`}
                              />
                              {produto.peso_cx && (
                                <p className="text-xs text-blue-600 mt-1">
                                  ℹ️ Auto-preenchido do cadastro. Pode editar se necessário.
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      }
                    })()
                  )}
                  {(() => {
                    if (produto.unidade === 'UN') {
                      // Para produtos UN, mostrar apenas se tem preço preenchido
                      const precoKgUn = parseFloat(precos[produtoId]?.preco_kg_un) || 0;
                      if (precoKgUn > 0) {
                        return (
                          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="text-sm space-y-1">
                              <p className="text-gray-700">
                                <strong>💰 Preço final:</strong>
                              </p>
                              <p className="text-gray-600">
                                <span className="font-bold text-green-700">
                                  R$ {precoKgUn.toFixed(2)}/kg ou /un
                                </span>
                              </p>
                              <p className="text-xs text-gray-500 italic">
                                (Sem cálculos adicionais - preço final do produto)
                              </p>
                            </div>
                          </div>
                        );
                      }
                    } else {
                      // Para produtos CX/SC, calcular preço por kg
                      const valorCaixa = parseFloat(precos[produtoId]?.valor_caixa) || 0;
                      const pesoCaixa = parseFloat(precos[produtoId]?.quantidade_kg) || 0;
                      const temValores = precos[produtoId]?.valor_caixa && precos[produtoId]?.quantidade_kg;
                      
                      if (temValores && pesoCaixa > 0 && valorCaixa > 0) {
                        const valorPorKg = valorCaixa / pesoCaixa;
                        return (
                          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="text-sm space-y-1">
                              <p className="text-gray-700">
                                <strong>💰 Cálculo automático:</strong>
                              </p>
                              <p className="text-gray-600">
                                R$ {valorCaixa.toFixed(2)} ÷ {pesoCaixa.toFixed(2)}kg = 
                                <span className="font-bold text-green-700 ml-1">
                                  R$ {valorPorKg.toFixed(2)}/kg
                                </span>
                              </p>
                            </div>
                          </div>
                        );
                      } else if (temValores && pesoCaixa === 0) {
                        return (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">
                              ⚠️ Peso da caixa não pode ser zero
                            </p>
                          </div>
                        );
                      } else if (temValores && valorCaixa === 0) {
                        return (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">
                              ⚠️ Valor da caixa não pode ser zero
                            </p>
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>
              );
            })}
        </div>
        
        <div className="mt-6 flex space-x-4">
          {(() => {
            // Verificar se há produtos com dados incompletos (iniciados mas não finalizados)
            let erroValidacao = null;
            
            for (const [produtoId, preco] of Object.entries(precos)) {
              const produto = produtos.find(p => p.id === produtoId);
              if (!produto) continue;
              
              if (produto.unidade === 'UN') {
                // Produto UN: se começou a preencher preco_kg_un, deve finalizar
                const precoKgUn = preco.preco_kg_un?.toString().trim();
                if (precoKgUn && parseFloat(precoKgUn) <= 0) {
                  erroValidacao = `${produto.nome} (UN): preço deve ser maior que zero`;
                  break;
                }
              } else {
                // Produto CX/SC: verificar se REALMENTE começou a preencher
                // Considerar apenas VALOR CX como indicador de "iniciou"
                // Peso CX pode estar auto-preenchido sem que o usuário tenha iniciado
                const valorCaixa = preco.valor_caixa?.toString().trim();
                const pesoCaixa = preco.quantidade_kg?.toString().trim();
                
                const temValor = valorCaixa && valorCaixa !== '' && parseFloat(valorCaixa) > 0;
                const temPeso = pesoCaixa && pesoCaixa !== '' && parseFloat(pesoCaixa) > 0;
                
                // REGRA CORRIGIDA: Se preencheu VALOR, deve preencher PESO também
                // Mas se só tem PESO (auto-preenchido), não é erro
                if (temValor && !temPeso) {
                  erroValidacao = `${produto.nome} (${produto.unidade}): preencha o PESO CX também`;
                  break;
                }
                
                // Se preencheu PESO mas não VALOR, avisar gentilmente mas permitir
                // (Peso pode estar auto-preenchido, usuário pode não querer enviar esse produto)
              }
            }
            
            // Contar produtos com dados completos
            const produtosCompletos = Object.entries(precos).filter(([produtoId, preco]) => {
              const produto = produtos.find(p => p.id === produtoId);
              if (!produto) return false;
              
              if (produto.unidade === 'UN') {
                const precoKgUn = parseFloat(preco.preco_kg_un) || 0;
                return precoKgUn > 0;
              } else {
                const valorCaixa = parseFloat(preco.valor_caixa) || 0;
                const pesoCaixa = parseFloat(preco.quantidade_kg) || 0;
                return valorCaixa > 0 && pesoCaixa > 0;
              }
            }).length;
            
            const podeEnviar = !erroValidacao; // Pode enviar mesmo sem nenhum produto (proposta vazia)

            return (
              <button
                onClick={handleSubmitPrices}
                disabled={!podeEnviar}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  podeEnviar 
                    ? 'bg-green-600 text-white hover:bg-green-700 cursor-pointer' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="submit-prices-button"
                title={erroValidacao || (produtosCompletos > 0 ? `Enviar proposta com ${produtosCompletos} produto(s)` : 'Enviar proposta (produtos vazios = não disponíveis)')}
              >
                {podeEnviar 
                  ? produtosCompletos > 0 
                    ? `Enviar Proposta (${produtosCompletos} produto${produtosCompletos > 1 ? 's' : ''})` 
                    : 'Enviar Proposta (Nenhum produto)'
                  : erroValidacao ? 'Complete os campos iniciados' : 'Enviar Proposta'
                }
              </button>
            );
          })()}
          <button
            onClick={() => {
              setShowSubmitPrice(false);
              setSelectedCotacao(null);
              setPrecos({});
            }}
            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            data-testid="cancel-submit-prices"
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  // Tela de Visualização de Ganhadores para Comprador
  if (showCotacaoDetail && selectedCotacao && temPermissaoComprador()) {
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => {
              setShowCotacaoDetail(false);
              setSelectedCotacao(null);
              setSearchTermCotacao(''); // Limpar busca ao fechar
            }}
            className="text-green-700 hover:text-green-800 font-medium"
            data-testid="back-from-cotacao-detail"
          >
            ← Voltar
          </button>
        </div>
        
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-900">{selectedCotacao.numero}</h3>
          <p className="text-gray-600 mt-1">
            Criada em: {formatarDataHoraBrasilia(selectedCotacao.criado_em)}
          </p>
          <p className="text-gray-600">
            Status: <span className={`px-2 py-1 rounded-full text-xs ${
              selectedCotacao.status_cotacao === 'Ativa' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {selectedCotacao.status_cotacao === 'Ativa' ? 'Em Andamento' : selectedCotacao.status_cotacao}
            </span>
          </p>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-900">Ganhadores por Produto</h4>
            <p className="text-sm text-gray-500 mt-1">
              Lista atualizada em tempo real conforme fornecedores enviam propostas
            </p>
            
            {/* Barra de pesquisa */}
            <div className="mt-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Pesquisar produto..."
                  value={searchTermCotacao}
                  onChange={(e) => setSearchTermCotacao(e.target.value)}
                  className="w-full px-4 py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {searchTermCotacao && (
                  <button
                    onClick={() => setSearchTermCotacao('')}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            
            {/* Botão Upload NF-e - Apenas para cotações encerradas */}
            {selectedCotacao.status_cotacao === 'Encerrada' && temPermissaoComprador() && (
              <div className="mt-4">
                <input
                  type="file"
                  ref={nfeFileInputRef}
                  onChange={handleNfeFileChange}
                  accept=".xml,.pdf"
                  className="hidden"
                  data-testid="nfe-file-input"
                />
                <button
                  onClick={handleUploadNfeClick}
                  disabled={nfeUploading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
                  data-testid="upload-nfe-button"
                >
                  {nfeUploading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Processando...
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload NF-e (XML/PDF)
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Importe XML ou PDF de nota fiscal para preencher preços de produtos sem vencedor
                </p>
              </div>
            )}
          </div>
          
          <div className="p-6">
            {selectedCotacao.produtos_ids && selectedCotacao.produtos_ids.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PRODUTO
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PREÇO (KG/UN)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        FORNECEDOR VENCEDOR
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        AÇÕES
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                {selectedCotacao.produtos_ids
                  .filter((produtoId) => {
                    // Filtrar produtos baseado no termo de busca
                    if (!searchTermCotacao) return true;
                    
                    const produto = produtos.find(p => p.id === produtoId);
                    if (!produto) return false;
                    
                    // Busca case-insensitive no nome do produto
                    return produto.nome.toLowerCase().includes(searchTermCotacao.toLowerCase());
                  })
                  .map((produtoId) => {
                  // PRIORIDADE 1: Verificar se existe vencedor manual (editado pelo COMPRADOR)
                  const vencedorManual = selectedCotacao.vencedores?.[produtoId];
                  
                  let melhorOferta = null;
                  let fornecedorVencedor = null;
                  
                  const produto = produtos.find(p => p.id === produtoId);
                  
                  if (vencedorManual) {
                    // Usar dados do vencedor manual (editado)
                    if (produto && produto.unidade === 'UN') {
                      // Produto UN: vencedor tem preco_kg_un
                      melhorOferta = {
                        preco_kg_un: vencedorManual.preco_kg_un || vencedorManual.valor_caixa, // fallback
                        valorPorKg: vencedorManual.preco_kg_un || vencedorManual.valor_caixa
                      };
                    } else {
                      // Produto CX/SC: vencedor tem valor_caixa e quantidade_kg
                      melhorOferta = {
                        valor_caixa: vencedorManual.valor_caixa,
                        quantidade_kg: vencedorManual.quantidade_kg,
                        valorPorKg: vencedorManual.valor_caixa / vencedorManual.quantidade_kg
                      };
                    }
                    fornecedorVencedor = vencedorManual.nome_fornecedor;
                  } else {
                    // PRIORIDADE 2: Calcular automaticamente o melhor preço das propostas originais
                    const temVariacoes = produto && produto.variacoes && produto.variacoes.length > 0;
                    
                    Object.entries(selectedCotacao.fornecedores || {}).forEach(([fornecedorId, fornecedorData]) => {
                      if (fornecedorData.enviado && fornecedorData.produtos && fornecedorData.produtos[produtoId]) {
                        const oferta = fornecedorData.produtos[produtoId];
                        
                        let valorPorKg = null;
                        let dadosOferta = null;
                        
                        // CORREÇÃO: Tratar produtos com VARIAÇÕES (inclui UN com variações via oferta.isUN)
                        if ((temVariacoes && oferta.variacoes) || (oferta.isUN && oferta.variacoes)) {
                          // Produto com variações (CX/SC ou UN): encontrar a variação com menor preço
                          const isUN = oferta.isUN || produto.unidade === 'UN';
                          const pesoCx = isUN ? 1 : (oferta.peso_cx || produto.peso_cx || 1);
                          Object.entries(oferta.variacoes).forEach(([nomeVariacao, variacaoData]) => {
                            if (variacaoData.valor_caixa && variacaoData.valor_caixa > 0) {
                              const valorPorKgVariacao = variacaoData.valor_caixa / pesoCx;
                              if (!valorPorKg || valorPorKgVariacao < valorPorKg) {
                                valorPorKg = valorPorKgVariacao;
                                dadosOferta = {
                                  valor_caixa: variacaoData.valor_caixa,
                                  quantidade_kg: pesoCx,
                                  valorPorKg: valorPorKgVariacao,
                                  variacao: nomeVariacao,
                                  isUN: isUN
                                };
                              }
                            }
                          });
                        } else if (produto && produto.unidade === 'UN') {
                          // Produto UN: usar preco_kg_un diretamente
                          if (oferta.preco_kg_un && oferta.preco_kg_un > 0) {
                            valorPorKg = oferta.preco_kg_un;
                            dadosOferta = {
                              preco_kg_un: oferta.preco_kg_un,
                              valorPorKg: valorPorKg
                            };
                          }
                        } else {
                          // Produto CX/SC: calcular a partir de valor_caixa / quantidade_kg
                          if (oferta.quantidade_kg && oferta.quantidade_kg > 0) {
                            valorPorKg = oferta.valor_caixa / oferta.quantidade_kg;
                            dadosOferta = {
                              valor_caixa: oferta.valor_caixa,
                              quantidade_kg: oferta.quantidade_kg,
                              valorPorKg: valorPorKg
                            };
                          }
                        }
                        
                        if (valorPorKg && dadosOferta && (!melhorOferta || valorPorKg < melhorOferta.valorPorKg)) {
                          melhorOferta = dadosOferta;
                          fornecedorVencedor = fornecedorData.nome_fornecedor;
                        }
                      }
                    });
                  }
                  
                  // Nome do produto já foi buscado acima
                  const nomeProduto = produto ? produto.nome : `Carregando produto... (${produtoId.substring(0, 8)}...)`;
                  
                  // Coletar todas as propostas para este produto
                  const todasPropostas = [];
                  const temVariacoes = produto && produto.variacoes && produto.variacoes.length > 0;
                  
                  Object.entries(selectedCotacao.fornecedores || {}).forEach(([fornecedorId, fornecedorData]) => {
                    if (fornecedorData.enviado && fornecedorData.produtos && fornecedorData.produtos[produtoId]) {
                      const oferta = fornecedorData.produtos[produtoId];
                      
                      if ((temVariacoes && oferta.variacoes) || (oferta.isUN && oferta.variacoes)) {
                        // Produto com variações (CX/SC ou UN): criar uma proposta para cada variação
                        const isUN = oferta.isUN || produto.unidade === 'UN';
                        const pesoCx = isUN ? 1 : (oferta.peso_cx || produto.peso_cx || 1);
                        Object.entries(oferta.variacoes).forEach(([nomeVariacao, variacaoData]) => {
                          if (variacaoData.valor_caixa && variacaoData.valor_caixa > 0) {
                            const isVariacaoVencedora = selectedCotacao.vencedores?.[produtoId]?.fornecedor_id === fornecedorId 
                              && selectedCotacao.vencedores?.[produtoId]?.variacao === nomeVariacao;
                            todasPropostas.push({
                              fornecedor_id: fornecedorId,
                              fornecedor: fornecedorData.nome_fornecedor,
                              variacao: nomeVariacao,
                              valor_caixa: variacaoData.valor_caixa,
                              quantidade_kg: pesoCx,
                              valorPorKg: variacaoData.valor_caixa / pesoCx,
                              isVencedor: isVariacaoVencedora,
                              isUN: isUN
                            });
                          }
                        });
                      } else if (produto && produto.unidade === 'UN') {
                        // Produto UN: verificar preco_kg_un
                        if (oferta.preco_kg_un && oferta.preco_kg_un > 0) {
                          todasPropostas.push({
                            fornecedor_id: fornecedorId,
                            fornecedor: fornecedorData.nome_fornecedor,
                            preco_kg_un: oferta.preco_kg_un,
                            valorPorKg: oferta.preco_kg_un,
                            isVencedor: fornecedorData.nome_fornecedor === fornecedorVencedor,
                            isUN: true
                          });
                        }
                      } else {
                        // Produto CX/SC sem variações: verificar valor_caixa e quantidade_kg
                        if (oferta.quantidade_kg && oferta.quantidade_kg > 0) {
                          todasPropostas.push({
                            fornecedor_id: fornecedorId,
                            fornecedor: fornecedorData.nome_fornecedor,
                            valor_caixa: oferta.valor_caixa,
                            quantidade_kg: oferta.quantidade_kg,
                            valorPorKg: oferta.valor_caixa / oferta.quantidade_kg,
                            isVencedor: fornecedorData.nome_fornecedor === fornecedorVencedor,
                            isUN: false
                          });
                        }
                      }
                    }
                  });
                  
                  // Ordenar propostas por valor (menor primeiro)
                  todasPropostas.sort((a, b) => a.valorPorKg - b.valorPorKg);
                  
                  return (
                    <React.Fragment key={produtoId}>
                      <tr className="hover:bg-gray-50">
                        {/* Coluna PRODUTO */}
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{nomeProduto}</div>
                          {produto && produto.unidade && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded mt-1 inline-block">
                              {produto.unidade}
                            </span>
                          )}
                        </td>
                      
                      {/* Coluna PREÇO (KG/UN) */}
                      <td className="px-6 py-4">
                        {melhorOferta ? (
                          <div>
                            <div className="text-sm font-semibold text-green-700">
                              R$ {melhorOferta.valorPorKg.toFixed(2)}/{produto && produto.unidade === 'UN' ? 'un' : 'kg'}
                            </div>
                            {!melhorOferta.preco_kg_un && melhorOferta.valor_caixa && (
                              <div className="text-xs text-gray-500 mt-1">
                                R$ {melhorOferta.valor_caixa.toFixed(2)}/cx • {melhorOferta.quantidade_kg}kg/cx
                              </div>
                            )}
                            {/* Mostrar variação se houver */}
                            {melhorOferta.variacao && (
                              <span className="mt-1 inline-block text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                {melhorOferta.variacao}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Sem proposta</span>
                        )}
                      </td>
                      
                      {/* Coluna FORNECEDOR VENCEDOR */}
                      <td className="px-6 py-4">
                        {melhorOferta ? (
                          <div>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {fornecedorVencedor}
                            </span>
                            {vencedorManual && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                ✏️ Editado
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">-</span>
                        )}
                      </td>
                      
                      {/* Coluna AÇÕES */}
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {/* Botão VER PROPOSTAS - Mostrar quando há propostas (inclusive com variações) */}
                          {todasPropostas.length > 0 && (
                            <button
                              onClick={() => setOutrasPropostasAberto(prev => ({
                                ...prev,
                                [produtoId]: !prev[produtoId]
                              }))}
                              className="px-3 py-1 text-xs font-medium text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200"
                            >
                              Ver propostas ({todasPropostas.length})
                            </button>
                          )}
                          
                          {/* Botão EDITAR VENCEDOR - Apenas em cotações encerradas COM propostas */}
                          {melhorOferta && selectedCotacao.status_cotacao === 'Encerrada' && (
                            <button
                              onClick={() => handleEditarVencedor(selectedCotacao, produto || {id: produtoId, nome: nomeProduto})}
                              className="px-3 py-1 text-xs font-medium text-green-700 hover:text-green-800 bg-green-50 hover:bg-green-100 rounded border border-green-200"
                            >
                              Editar vencedor
                            </button>
                          )}
                          
                          {/* Botão ADICIONAR VENCEDOR - Apenas em cotações encerradas SEM propostas */}
                          {!melhorOferta && selectedCotacao.status_cotacao === 'Encerrada' && (
                            <button
                              onClick={() => handleAdicionarVencedorManual(selectedCotacao, produto || {id: produtoId, nome: nomeProduto})}
                              className="px-3 py-1 text-xs font-medium text-orange-700 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 rounded border border-orange-200"
                            >
                              + Adicionar vencedor
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Linha expandida com dropdown de propostas */}
                    {outrasPropostasAberto[produtoId] && todasPropostas.length > 0 && (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 bg-gray-50">
                          <div className="space-y-2">
                            <h6 className="font-medium text-gray-700 text-sm mb-3">
                              Todas as propostas para {nomeProduto}:
                            </h6>
                            {todasPropostas.map((proposta, index) => (
                              <div 
                                key={index}
                                className={`p-3 rounded-lg ${
                                  proposta.isVencedor 
                                    ? 'bg-green-50 border-2 border-green-400' 
                                    : 'bg-white border border-gray-200'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium text-sm">
                                        {proposta.fornecedor}
                                        {proposta.variacao && (
                                          <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                            {proposta.variacao}
                                          </span>
                                        )}
                                      </span>
                                      {proposta.isVencedor && (
                                        <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                                          ✓ Vencedor Atual
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-700">
                                      <span className="font-semibold">R$ {proposta.valorPorKg.toFixed(2)}/{produto && produto.unidade === 'UN' ? 'un' : 'kg'}</span>
                                    </div>
                                    {proposta.isUN ? (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Preço final: R$ {(proposta.preco_kg_un || proposta.valor_caixa)?.toFixed(2)}/un
                                      </div>
                                    ) : (
                                      <div className="text-xs text-gray-500 mt-1">
                                        R$ {proposta.valor_caixa?.toFixed(2)}/caixa • {proposta.quantidade_kg}kg/caixa
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Botão para selecionar como vencedor */}
                                  {!proposta.isVencedor && selectedCotacao.status_cotacao === 'Encerrada' && temPermissaoComprador() && (
                                    <button
                                      onClick={async () => {
                                        try {
                                          await axios.put(
                                            `/cotacoes/${selectedCotacao.id}/produtos/${produtoId}/vencedor`,
                                            null,
                                            {
                                              params: {
                                                fornecedor_id: proposta.fornecedor_id,
                                                variacao: proposta.variacao || undefined
                                              }
                                            }
                                          );
                                          toast({
                                            title: "Vencedor alterado com sucesso!",
                                            description: `${proposta.fornecedor}${proposta.variacao ? ` (${proposta.variacao})` : ''} agora é o vencedor de ${nomeProduto}`
                                          });
                                          
                                          // Recarregar apenas a cotação atual (mantendo na tela)
                                          await recarregarCotacaoAtual(selectedCotacao.id);
                                        } catch (error) {
                                          toast({
                                            title: "Erro ao alterar vencedor",
                                            description: error.response?.data?.detail || "Erro interno",
                                            variant: "destructive"
                                          });
                                        }
                                      }}
                                      className="ml-3 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 whitespace-nowrap"
                                    >
                                      Selecionar como vencedor
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
                  </tbody>
                </table>
                
                {/* Mensagem quando não há resultados na busca */}
                {searchTermCotacao && selectedCotacao.produtos_ids.filter((produtoId) => {
                  const produto = produtos.find(p => p.id === produtoId);
                  return produto && produto.nome.toLowerCase().includes(searchTermCotacao.toLowerCase());
                }).length === 0 && (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-gray-500 mt-2">Nenhum produto encontrado.</p>
                    <p className="text-sm text-gray-400 mt-1">Tente outro termo de busca</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nenhum produto nesta cotação</p>
            )}
          </div>
        </div>
        
        {/* Modal Editar Vencedor */}
        {showEditarVencedorModal && produtoEditando && cotacaoEditando && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-semibold">Editar Vencedor - {produtoEditando.nome}</h3>
                <button onClick={() => setShowEditarVencedorModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
              </div>
              
              <div className="p-6">
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Instruções:</strong> Edite os dados do vencedor para este produto. 
                    Útil para ajustar resultados baseados em negociações externas.
                  </p>
                </div>
                
                <div className="space-y-4">
                  {/* Selecionar Fornecedor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fornecedor Vencedor *
                    </label>
                    <select
                      value={vencedorEditando.fornecedor_id}
                      onChange={(e) => handleChangeFornecedorVencedor(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                      required
                    >
                      <option value="">Selecione um fornecedor</option>
                      {fornecedoresVinculados.map(forn => (
                        <option key={forn.id} value={forn.id}>
                          {forn.nome_completo}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Apenas fornecedores vinculados a este produto estão disponíveis
                    </p>
                  </div>
                  
                  {/* Campos condicionais baseados no tipo de produto */}
                  {produtoEditando && produtoEditando.unidade === 'UN' ? (
                    // Produto UN: apenas PREÇO KG/UN
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preço KG/UN (R$) - Preço final *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={vencedorEditando.preco_kg_un || vencedorEditando.valor_caixa || 0}
                        onChange={(e) => setVencedorEditando({
                          ...vencedorEditando, 
                          preco_kg_un: e.target.value,
                          valor_caixa: e.target.value // Para compatibilidade
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                        placeholder="Ex: 12.50"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Informe o preço final por kg ou por unidade (sem cálculos)
                      </p>
                    </div>
                  ) : (
                    // Produto CX/SC: VALOR CAIXA e PESO CAIXA
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor da Caixa (R$) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={vencedorEditando.valor_caixa}
                          onChange={(e) => setVencedorEditando({...vencedorEditando, valor_caixa: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                          placeholder="Ex: 25.00"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Peso da Caixa (KG) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={vencedorEditando.quantidade_kg}
                          onChange={(e) => setVencedorEditando({...vencedorEditando, quantidade_kg: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                          placeholder="Ex: 10.5"
                          required
                        />
                      </div>
                      
                      {/* Preview do cálculo */}
                      {vencedorEditando.valor_caixa > 0 && vencedorEditando.quantidade_kg > 0 && (
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-700">
                            <strong>Valor por KG:</strong> R$ {(parseFloat(vencedorEditando.valor_caixa) / parseFloat(vencedorEditando.quantidade_kg)).toFixed(2)}/kg
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowEditarVencedorModal(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSalvarVencedor}
                    className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
                  >
                    Salvar Alterações
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal Adicionar Vencedor Manual (para produtos SEM propostas) */}
        {showAdicionarVencedorManualModal && produtoEditando && cotacaoEditando && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-orange-700">Adicionar Vencedor - {produtoEditando.nome}</h3>
                <button onClick={() => setShowAdicionarVencedorManualModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
              </div>
              
              <div className="p-6">
                <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    <strong>Atenção:</strong> Este produto não recebeu propostas pelos fornecedores no app. 
                    Use este formulário para registrar uma negociação feita externamente (fora do sistema).
                  </p>
                </div>
                
                <div className="space-y-4">
                  {/* Fornecedor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fornecedor *
                    </label>
                    <div className="space-y-2">
                      {/* Selecionar de fornecedores cadastrados */}
                      <select
                        value={vencedorManualDados.fornecedor_id}
                        onChange={(e) => {
                          const fornecedor = todosFornecedores.find(f => f.id === e.target.value);
                          setVencedorManualDados({
                            ...vencedorManualDados,
                            fornecedor_id: e.target.value,
                            fornecedor_nome: fornecedor ? fornecedor.nome_completo : ''
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Selecione um fornecedor cadastrado...</option>
                        {todosFornecedores.map(forn => (
                          <option key={forn.id} value={forn.id}>
                            {forn.nome_completo}
                          </option>
                        ))}
                      </select>
                      
                      {/* Ou digitar manualmente */}
                      <div className="text-center text-xs text-gray-500">OU</div>
                      <input
                        type="text"
                        value={vencedorManualDados.fornecedor_nome}
                        onChange={(e) => setVencedorManualDados({
                          ...vencedorManualDados,
                          fornecedor_id: '', // Limpar seleção ao digitar manualmente
                          fornecedor_nome: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        placeholder="Digite o nome do fornecedor manualmente"
                      />
                    </div>
                  </div>
                  
                  {/* Variação (se produto tiver variações) */}
                  {produtoEditando && produtoEditando.variacoes && produtoEditando.variacoes.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Variação *
                      </label>
                      <select
                        value={vencedorManualDados.variacao}
                        onChange={(e) => setVencedorManualDados({...vencedorManualDados, variacao: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        required
                      >
                        <option value="">Selecione a variação</option>
                        {produtoEditando.variacoes.map(variacao => (
                          <option key={variacao} value={variacao}>
                            {variacao}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Campos de preço baseados no tipo de produto */}
                  {produtoEditando && produtoEditando.unidade === 'UN' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preço por UN (R$) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={vencedorManualDados.valor_caixa || ''}
                        onChange={(e) => setVencedorManualDados({...vencedorManualDados, valor_caixa: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        placeholder="Ex: 5.00"
                        required
                      />
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor da Caixa (R$) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={vencedorManualDados.valor_caixa || ''}
                          onChange={(e) => setVencedorManualDados({...vencedorManualDados, valor_caixa: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                          placeholder="Ex: 50.00"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Peso da Caixa (KG) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={vencedorManualDados.quantidade_kg || ''}
                          onChange={(e) => setVencedorManualDados({...vencedorManualDados, quantidade_kg: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                          placeholder="Ex: 20.00"
                          required
                        />
                      </div>
                      
                      {/* Preview do cálculo */}
                      {vencedorManualDados.valor_caixa > 0 && vencedorManualDados.quantidade_kg > 0 && (
                        <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                          <p className="text-sm text-orange-700">
                            <strong>Valor por KG:</strong> R$ {(parseFloat(vencedorManualDados.valor_caixa) / parseFloat(vencedorManualDados.quantidade_kg)).toFixed(2)}/kg
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowAdicionarVencedorManualModal(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSalvarVencedorManual}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    Adicionar Vencedor
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal Upload NF-e - Matching de Produtos */}
        {showUploadNfeModal && nfeData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-blue-700">
                    Importar NF-e ({nfeData.source === 'pdf_ocr' ? 'PDF via OCR' : 'XML'})
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Fornecedor: <strong>{nfeData.nfe.fornecedor.nome_exibicao || nfeData.nfe.fornecedor.nome}</strong>
                    {nfeData.nfe.nota.numero && (
                      <span className="ml-2">| NF nº {nfeData.nfe.nota.numero}</span>
                    )}
                    {nfeData.source === 'pdf_ocr' && (
                      <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                        Dados extraídos via OCR - verifique os valores
                      </span>
                    )}
                  </p>
                </div>
                <button 
                  onClick={() => {
                    setShowUploadNfeModal(false);
                    setNfeData(null);
                    setNfeMatches([]);
                  }} 
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto flex-1">
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Instruções:</strong> Vincule cada produto da NF-e a um produto da cotação. 
                    Produtos sem vínculo serão ignorados. Para produtos que já têm vencedor, marque "Sobrescrever" se deseja substituir.
                  </p>
                </div>
                
                <div className="space-y-4">
                  {nfeMatches.map((match, index) => {
                    const produtoCotacao = nfeData.cotacao.produtos.find(p => p.id === match.produto_cotacao_id);
                    const temVencedor = produtoCotacao?.tem_vencedor;
                    
                    return (
                      <div 
                        key={index}
                        className={`p-4 rounded-lg border ${
                          match.produto_cotacao_id 
                            ? (temVencedor ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300')
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
                              Produto na NF-e
                            </label>
                            <div className="bg-white p-3 rounded border border-gray-200">
                              <p className="font-medium text-gray-900">{match.produto_nfe.nome}</p>
                              <div className="flex flex-wrap gap-2 mt-2 text-sm">
                                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                  R$ {match.produto_nfe.valor_unitario?.toFixed(2) || '0.00'}
                                </span>
                                <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                  Qtd: {match.produto_nfe.quantidade || 0}
                                </span>
                                <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                  {match.produto_nfe.unidade || 'UN'}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">
                              Vincular a Produto da Cotação
                            </label>
                            <select
                              value={match.produto_cotacao_id}
                              onChange={(e) => handleNfeMatchChange(index, 'produto_cotacao_id', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                              data-testid={`nfe-match-select-${index}`}
                            >
                              <option value="">-- Não vincular (ignorar) --</option>
                              {nfeData.cotacao.produtos.map(prod => (
                                <option key={prod.id} value={prod.id}>
                                  {prod.nome} ({prod.unidade})
                                  {prod.tem_vencedor ? ` [Já tem vencedor: ${prod.vencedor_atual}]` : ' [Sem vencedor]'}
                                </option>
                              ))}
                            </select>
                            
                            {match.auto_matched && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ Match automático sugerido
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {match.produto_cotacao_id && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              {produtoCotacao && produtoCotacao.unidade !== 'UN' && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    Peso Caixa (KG)
                                  </label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={match.quantidade_kg}
                                    onChange={(e) => handleNfeMatchChange(index, 'quantidade_kg', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    placeholder={produtoCotacao?.peso_cx ? `Padrão: ${produtoCotacao.peso_cx}` : 'Ex: 20'}
                                  />
                                </div>
                              )}
                              
                              {produtoCotacao && produtoCotacao.variacoes && produtoCotacao.variacoes.length > 0 && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-500 mb-1">
                                    Variação *
                                  </label>
                                  <select
                                    value={match.variacao}
                                    onChange={(e) => handleNfeMatchChange(index, 'variacao', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    required
                                  >
                                    <option value="">Selecione...</option>
                                    {produtoCotacao.variacoes.map(v => (
                                      <option key={v} value={v}>{v}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              
                              {temVencedor && (
                                <div className="flex items-center">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={match.sobrescrever}
                                      onChange={(e) => handleNfeMatchChange(index, 'sobrescrever', e.target.checked)}
                                      className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                                    />
                                    <span className="text-sm text-yellow-700">
                                      Sobrescrever vencedor atual
                                    </span>
                                  </label>
                                </div>
                              )}
                            </div>
                            
                            {temVencedor && !match.sobrescrever && (
                              <p className="text-xs text-yellow-600 mt-2">
                                ⚠️ Este produto já tem vencedor ({produtoCotacao.vencedor_atual}). Marque "Sobrescrever" para substituir.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Resumo</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Total de itens NF-e:</span>
                      <p className="font-semibold">{nfeMatches.length}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Vinculados:</span>
                      <p className="font-semibold text-green-600">
                        {nfeMatches.filter(m => m.produto_cotacao_id).length}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Ignorados:</span>
                      <p className="font-semibold text-gray-500">
                        {nfeMatches.filter(m => !m.produto_cotacao_id).length}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Com sobrescrita:</span>
                      <p className="font-semibold text-yellow-600">
                        {nfeMatches.filter(m => m.produto_cotacao_id && m.sobrescrever).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border-t px-6 py-4 flex justify-end space-x-3 bg-gray-50">
                <button
                  onClick={() => {
                    setShowUploadNfeModal(false);
                    setNfeData(null);
                    setNfeMatches([]);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAplicarNfe}
                  disabled={nfeApplying || nfeMatches.filter(m => m.produto_cotacao_id).length === 0}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="aplicar-nfe-button"
                >
                  {nfeApplying ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Aplicando...
                    </>
                  ) : (
                    <>Aplicar NF-e ({nfeMatches.filter(m => m.produto_cotacao_id).length} produtos)</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Modal de visualização de resultados para FORNECEDORES
  if (showCotacaoDetail && selectedCotacao && usuario.tipo_usuario === 'Fornecedor') {
    // Se produtos ainda não foram carregados, mostrar loading
    if (produtos.length === 0) {
      return (
        <div>
          <div className="mb-6">
            <button
              onClick={() => {
                setShowCotacaoDetail(false);
                setSelectedCotacao(null);
              }}
              className="text-green-700 hover:text-green-800 font-medium"
            >
              ← Voltar
            </button>
          </div>
          
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Resultado - {selectedCotacao.numero}</h3>
            <p className="text-gray-600 mt-1">
              Encerrada em: {formatarDataHoraBrasilia(selectedCotacao.criado_em)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center py-12">
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600">Carregando produtos...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Buscar produtos que o fornecedor ganhou
    const produtosVencidos = [];
    
    if (selectedCotacao.produtos_ids && selectedCotacao.produtos_ids.length > 0) {
      selectedCotacao.produtos_ids.forEach((produtoId) => {
        // Verificar se este fornecedor ganhou este produto
        const vencedorManual = selectedCotacao.vencedores?.[produtoId];
        
        // Se há vencedor manual, verificar se é este fornecedor
        if (vencedorManual && vencedorManual.fornecedor_id === usuario.id) {
          const produto = produtos.find(p => p.id === produtoId);
          // Adicionar ao array de vencidos SEMPRE que for vencedor, mesmo se produto não for encontrado
          produtosVencidos.push({
            produto: produto || { id: produtoId, nome: `Produto ${produtoId.substring(0, 8)}...`, unidade: 'N/A' },
            preco: vencedorManual.preco_kg_un || (vencedorManual.valor_caixa && vencedorManual.quantidade_kg ? vencedorManual.valor_caixa / vencedorManual.quantidade_kg : 0),
            valor_caixa: vencedorManual.valor_caixa,
            quantidade_kg: vencedorManual.quantidade_kg,
            preco_kg_un: vencedorManual.preco_kg_un
          });
        } else if (!vencedorManual) {
          // Se não há vencedor manual, calcular automaticamente
          let melhorOferta = null;
          let fornecedorVencedorId = null;
          
          Object.entries(selectedCotacao.fornecedores || {}).forEach(([fornecedorId, fornecedorData]) => {
            if (fornecedorData.enviado && fornecedorData.produtos && fornecedorData.produtos[produtoId]) {
              const oferta = fornecedorData.produtos[produtoId];
              const produto = produtos.find(p => p.id === produtoId);
              
              let valorPorKg = null;
              
              if (produto && produto.unidade === 'UN') {
                if (oferta.preco_kg_un && oferta.preco_kg_un > 0) {
                  valorPorKg = oferta.preco_kg_un;
                }
              } else {
                if (oferta.quantidade_kg && oferta.quantidade_kg > 0) {
                  valorPorKg = oferta.valor_caixa / oferta.quantidade_kg;
                }
              }
              
              if (valorPorKg && (!melhorOferta || valorPorKg < melhorOferta)) {
                melhorOferta = valorPorKg;
                fornecedorVencedorId = fornecedorId;
              }
            }
          });
          
          // Se este fornecedor é o vencedor automático
          if (fornecedorVencedorId === usuario.id) {
            const produto = produtos.find(p => p.id === produtoId);
            const oferta = selectedCotacao.fornecedores[usuario.id]?.produtos?.[produtoId];
            
            // Adicionar ao array de vencidos SEMPRE que for vencedor, mesmo se produto não for encontrado
            if (oferta) {
              produtosVencidos.push({
                produto: produto || { id: produtoId, nome: `Produto ${produtoId.substring(0, 8)}...`, unidade: 'N/A' },
                preco: melhorOferta,
                valor_caixa: oferta.valor_caixa,
                quantidade_kg: oferta.quantidade_kg,
                preco_kg_un: oferta.preco_kg_un
              });
            }
          }
        }
      });
    }
    
    return (
      <div>
        <div className="mb-6">
          <button
            onClick={() => {
              setShowCotacaoDetail(false);
              setSelectedCotacao(null);
            }}
            className="text-green-700 hover:text-green-800 font-medium"
            data-testid="back-from-resultado-fornecedor"
          >
            ← Voltar
          </button>
        </div>
        
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Resultado - {selectedCotacao.numero}</h3>
          <p className="text-gray-600 mt-1">
            Encerrada em: {formatarDataHoraBrasilia(selectedCotacao.criado_em)}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          {produtosVencidos.length > 0 ? (
            <div>
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-medium text-green-800 mb-2">🎉 Parabéns! Você ganhou {produtosVencidos.length} produto(s)</h4>
                <p className="text-sm text-green-700">
                  Confira abaixo os produtos que você conquistou nesta cotação:
                </p>
              </div>
              
              <div className="space-y-4">
                {produtosVencidos.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border-2 border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-lg font-medium text-gray-900">{item.produto.nome}</h5>
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        ✓ VENCEDOR
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 mb-1"><strong>Unidade:</strong></p>
                        <p className="text-gray-900">{item.produto.unidade}</p>
                      </div>
                      
                      {item.produto.unidade === 'UN' ? (
                        <div>
                          <p className="text-gray-600 mb-1"><strong>Preço vencedor:</strong></p>
                          <p className="text-lg font-semibold text-green-700">
                            R$ {item.preco_kg_un?.toFixed(2)}/un
                          </p>
                        </div>
                      ) : (
                        <>
                          <div>
                            <p className="text-gray-600 mb-1"><strong>Valor CX:</strong></p>
                            <p className="text-gray-900">R$ {item.valor_caixa?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 mb-1"><strong>Peso CX:</strong></p>
                            <p className="text-gray-900">{item.quantidade_kg} kg</p>
                          </div>
                          <div>
                            <p className="text-gray-600 mb-1"><strong>Preço por KG:</strong></p>
                            <p className="text-lg font-semibold text-green-700">
                              R$ {item.preco?.toFixed(2)}/kg
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">😔</div>
              <h4 className="text-xl font-medium text-gray-900 mb-2">
                Ops, infelizmente você não ganhou desta vez
              </h4>
              <p className="text-gray-600">
                Continue participando das próximas cotações. Sua chance está chegando!
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {temPermissaoComprador() && (
        <div className="mb-6">
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 flex items-center space-x-2"
            data-testid="add-cotacao-button"
          >
            <span>+</span>
            <span>Nova Cotação</span>
          </button>
        </div>
      )}
      
      {/* Seção de cotações EXPIRADAS aguardando seleção de variações */}
      {temPermissaoComprador() && (() => {
        const agora = new Date();
        const cotacoesAguardandoVariacoes = cotacoes.filter(cotacao => {
          if (cotacao.status_cotacao !== 'Ativa') return false;
          
          const dataExpiracao = new Date(cotacao.expira_em);
          if (dataExpiracao > agora) return false; // Ainda não expirou
          
          // Verificar se tem produtos com variações
          const produtosComVariacoes = produtos.filter(p => 
            cotacao.produtos_ids.includes(p.id) && 
            p.variacoes && 
            p.variacoes.length > 0
          );
          
          return produtosComVariacoes.length > 0;
        });
        
        if (cotacoesAguardandoVariacoes.length === 0) return null;
        
        return (
          <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <h3 className="text-lg font-bold text-red-800 mb-3 flex items-center gap-2">
              <span className="text-2xl">⚠️</span>
              Ação Necessária: Selecione Variações Vencedoras
            </h3>
            <p className="text-sm text-red-700 mb-4">
              As cotações abaixo expiraram e possuem produtos com variações. Clique em "Selecionar Variações" para encerrar.
            </p>
            <div className="space-y-3">
              {cotacoesAguardandoVariacoes.map(cotacao => (
                <div key={cotacao.id} className="bg-white border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {cotacao.nome && (
                        <h4 className="font-semibold text-gray-900">{cotacao.nome}</h4>
                      )}
                      <p className="text-sm text-gray-600">{cotacao.numero}</p>
                      <p className="text-xs text-red-600 mt-1">
                        ⏰ Expirou em: {formatarDataHoraBrasilia(cotacao.expira_em)}
                      </p>
                    </div>
                    <button
                      onClick={() => encerrarCotacao(cotacao)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium whitespace-nowrap"
                    >
                      Selecionar Variações
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
      
      {cotacoes.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-4">💲</div>
          <p>
            {usuario.tipo_usuario === 'Fornecedor' 
              ? 'Nenhuma cotação disponível para você.' 
              : 'Nenhuma cotação criada ainda.'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cotacoes.map((cotacao) => (
            <div key={cotacao.id} className="bg-gray-50 rounded-lg p-4 border">
              {cotacao.nome && (
                <h3 className="font-semibold text-gray-900 text-lg mb-1">{cotacao.nome}</h3>
              )}
              <h4 className="font-medium text-gray-600 mb-2 text-sm">{cotacao.numero}</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Criada em: {formatarDataHoraBrasilia(cotacao.criado_em)}</p>
                <p>Status: 
                  <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                    cotacao.status_cotacao === 'Ativa' 
                      ? 'bg-green-100 text-green-800' 
                      : cotacao.status_cotacao === 'Encerrada'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {cotacao.status_cotacao === 'Ativa' ? 'Em Andamento' : cotacao.status_cotacao}
                  </span>
                </p>
                {cotacao.status_cotacao === 'Ativa' && (
                  <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                    <p className="text-xs font-semibold text-orange-800">
                      ⏰ Encerra em: {formatarDataHoraBrasilia(cotacao.expira_em)}
                    </p>
                    <p className="text-xs text-orange-600 mt-0.5">
                      Tempo restante: {getTimeRemaining(cotacao.expira_em)}
                    </p>
                  </div>
                )}
                <p>Produtos: {cotacao.produtos_ids?.length || 0}</p>
                <p>Fornecedores: {Object.keys(cotacao.fornecedores || {}).length}</p>
                
                {/* Mostrar fornecedores pendentes apenas para COMPRADOR */}
                {temPermissaoComprador() && (() => {
                  const pendentes = getFornecedoresPendentes(cotacao);
                  if (pendentes.length > 0) {
                    return (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-xs font-semibold text-yellow-800 mb-1">
                          ⚠️ Fornecedores Pendentes ({pendentes.length}):
                        </p>
                        <div className="text-xs text-yellow-700 space-y-0.5">
                          {pendentes.map(forn => (
                            <div key={forn.id}>• {forn.nome_completo || forn.nome || 'Fornecedor'}</div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="mt-4 flex space-x-2">
                {usuario.tipo_usuario === 'Fornecedor' && (
                  <>
                    {cotacao.status_cotacao === 'Ativa' && !cotacao.fornecedores?.[usuario.id]?.enviado ? (
                      <button 
                        onClick={() => {
                          setSelectedCotacao(cotacao);
                          setShowSubmitPrice(true);
                        }}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                        data-testid={`submit-price-${cotacao.id}`}
                      >
                        Enviar Proposta
                      </button>
                    ) : cotacao.fornecedores?.[usuario.id]?.enviado && cotacao.status_cotacao === 'Ativa' ? (
                      <button 
                        onClick={() => {
                          setSelectedCotacao(cotacao);
                          setShowSubmitPrice(true);
                        }}
                        className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded text-sm hover:bg-yellow-200"
                        data-testid={`edit-proposal-${cotacao.id}`}
                      >
                        ✏️ Editar Proposta
                      </button>
                    ) : cotacao.status_cotacao === 'Encerrada' && cotacao.fornecedores?.[usuario.id]?.enviado ? (
                      <button 
                        onClick={() => {
                          setSelectedCotacao(cotacao);
                          setShowCotacaoDetail(true);
                        }}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200"
                        data-testid={`view-results-${cotacao.id}`}
                      >
                        Ver Resultados
                      </button>
                    ) : (
                      <button 
                        disabled
                        className="px-3 py-1 bg-gray-100 text-gray-500 rounded text-sm cursor-not-allowed"
                        data-testid={`cotacao-unavailable-${cotacao.id}`}
                      >
                        Não Disponível
                      </button>
                    )}
                  </>
                )}
                {temPermissaoComprador() && (
                  <>
                    <button 
                      onClick={() => handleViewCotacao(cotacao)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                      data-testid={`view-cotacao-${cotacao.id}`}
                    >
                      Visualizar
                    </button>
                    {cotacao.status_cotacao === 'Ativa' && !cotacao.baseada_em_pedidos && (
                      <button 
                        onClick={() => handleEditCotacao(cotacao)}
                        className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded text-sm hover:bg-indigo-200"
                        data-testid={`edit-cotacao-${cotacao.id}`}
                      >
                        Editar
                      </button>
                    )}
                    <button 
                      onClick={() => handleDeleteCotacao(cotacao)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                      data-testid={`delete-cotacao-${cotacao.id}`}
                    >
                      Excluir
                    </button>
                    {cotacao.status_cotacao === 'Ativa' && (
                      <>
                        {/* Botão "Enviar Cotação" removido - fornecedores acessam cotações diretamente pelo app */}
                        <button 
                          onClick={() => encerrarCotacao(cotacao)}
                          className="px-3 py-1 bg-orange-100 text-orange-700 rounded text-sm hover:bg-orange-200"
                          data-testid={`encerrar-cotacao-${cotacao.id}`}
                        >
                          Encerrar
                        </button>
                      </>
                    )}
                </>
              )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Modal de Seleção de Variações */}
      {showModalVariacoes && cotacaoParaEncerrar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                Selecionar Variações Vencedoras
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Cotação: {cotacaoParaEncerrar.numero} - {cotacaoParaEncerrar.nome}
              </p>
            </div>
            
            {/* Body - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <p className="text-sm text-gray-700 mb-6">
                Selecione qual variação de cada produto será considerada vencedora nesta cotação:
              </p>
              
              <div className="space-y-6">
                {produtos
                  .filter(p => 
                    cotacaoParaEncerrar.produtos_ids.includes(p.id) && 
                    p.variacoes && 
                    p.variacoes.length > 0
                  )
                  .map(produto => {
                    // Buscar propostas dos fornecedores para este produto
                    const propostas = [];
                    Object.entries(cotacaoParaEncerrar.fornecedores || {}).forEach(([fornId, fornData]) => {
                      if (fornData.enviado && fornData.produtos?.[produto.id]) {
                        const produtoData = fornData.produtos[produto.id];
                        if (produtoData.variacoes) {
                          Object.entries(produtoData.variacoes).forEach(([nomeVar, varData]) => {
                            propostas.push({
                              fornecedor: fornData.nome_fornecedor || fornId,
                              variacao: nomeVar,
                              valor_caixa: varData.valor_caixa,
                              peso_cx: produtoData.peso_cx,
                              preco_kg: produtoData.peso_cx > 0 ? (varData.valor_caixa / produtoData.peso_cx) : 0
                            });
                          });
                        }
                      }
                    });
                    
                    return (
                      <div key={produto.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                        <h3 className="font-bold text-lg text-gray-900 mb-3">
                          {produto.nome}
                        </h3>
                        
                        <div className="space-y-2">
                          {produto.variacoes.map((variacao, idx) => {
                            // Filtrar propostas desta variação
                            const propostasVariacao = propostas.filter(p => p.variacao === variacao);
                            
                            return (
                              <label 
                                key={idx}
                                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  variacoesSelecionadas[produto.id] === variacao
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-200 bg-white hover:border-green-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`variacao-${produto.id}`}
                                  value={variacao}
                                  checked={variacoesSelecionadas[produto.id] === variacao}
                                  onChange={(e) => {
                                    setVariacoesSelecionadas(prev => ({
                                      ...prev,
                                      [produto.id]: e.target.value
                                    }));
                                  }}
                                  className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">{variacao}</div>
                                  {propostasVariacao.length > 0 ? (
                                    <div className="mt-2 space-y-1">
                                      {propostasVariacao.map((prop, pidx) => (
                                        <div key={pidx} className="text-sm text-gray-600 flex justify-between">
                                          <span>{prop.fornecedor}:</span>
                                          <span className="font-semibold">
                                            R$ {prop.valor_caixa.toFixed(2)} 
                                            {prop.peso_cx > 0 && (
                                              <span className="text-green-700 ml-2">
                                                (R$ {prop.preco_kg.toFixed(2)}/kg)
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-sm text-gray-400 mt-1">
                                      Nenhuma proposta para esta variação
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowModalVariacoes(false);
                  setCotacaoParaEncerrar(null);
                  setVariacoesSelecionadas({});
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarEncerramentoComVariacoes}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Encerrar Cotação
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};


export default CotacaoTab;
